import React, { useEffect, useState } from "react";
import { User, FileText, Edit3, Save, Settings, Activity, Calendar, Star, Award } from "lucide-react";
import { useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import Form from "react-bootstrap/Form";
import profileImg from "../assets/images/user.jpg";
import logoImg from "../assets/images/logo.svg";
import SaveProfile from "../components/ProfileComp/SaveProfile";
import { getSelectedProfileData } from "../apiCalls/user";
import SaveLeagalInfo from "../components/ProfileComp/SaveLeagalInfo";
import AboutLab from "../components/ProfileComp/AboutLab";
import LabHighlights from "../components/ProfileComp/LabHighlights";
import { updateLabStatus } from "../apiCalls/user";
import ServiceList from "../components/ProfileComp/ServiceList";
import ProfileDocTab from "../components/ProfileComp/ProfileDocTab";
import LabTimings from "../components/ProfileComp/LabTimings";

const TABS = [
  { id: "profile", label: "Profile", icon: <User size={15} /> },
  { id: "services", label: "Services", icon: <Settings size={15} /> },
  { id: "documents", label: "Documents & Photos", icon: <FileText size={15} /> },
];

const LabProfilePage = () => {
  const location = useLocation();
  const labId = location.pathname.split("/")[2];

  const [labData, setLabData] = useState({});
  const [checked, setChecked] = useState(false);
  const [activeTab, setActiveTab] = useState("profile");
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    getSelectedProfileData(labId).then(setLabData);
  }, [checked, labId]);

  const handleChangeSwitch = async () => {
    const res = await updateLabStatus(labId);
    if (res?.data?.success) setChecked((c) => !c);
  };

  const saveProfile = () => setIsEditing(false);

  // derive initials from lab name
  const initials = labData?.labName
    ? labData.labName.split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase()
    : "LB";

  const statItems = [
    { icon: <Activity size={20} />, color: "#8166f4", bg: "#f3f0ff", value: "15,420", label: "Total Tests" },
    { icon: <Calendar size={20} />, color: "#10b981", bg: "#d1fae5", value: "1,250", label: "This Month" },
    { icon: <Star size={20} />, color: "#f59e0b", bg: "#fef3c7", value: "4.8", label: "Avg Rating" },
    { icon: <Award size={20} />, color: "#3b82f6", bg: "#dbeafe", value: "6 yrs", label: "Years Active" },
  ];

  return (
    <div className="pf-page">

      {/* ── Hero banner ─────────────────────────── */}
      <div className="pf-hero">
        {/* Brand pattern */}
        <div className="pf-hero-blob pf-hero-blob--1" />
        <div className="pf-hero-blob pf-hero-blob--2" />

        <div className="pf-hero-inner">
          {/* Lab identity */}
          <div className="pf-hero-identity">
            <div className="pf-hero-logo">
              <img src={logoImg} alt="logo" />
            </div>
            <div>
              <h2 className="pf-hero-name">{labData?.labName || "Lab Name"}</h2>
              <p className="pf-hero-owner">
                <User size={12} style={{ marginRight: 4 }} />
                {labData?.ownerName || "Owner Name"}
              </p>
              <div className="pf-hero-tags">
                <span className={`pf-tag ${labData?.labStatus ? "pf-tag--green" : "pf-tag--red"}`}>
                  <i className={`bi bi-circle-fill`} style={{ fontSize: 6 }} />
                  {labData?.labStatus ? "Active" : "Inactive"}
                </span>
                {labData?.labEmail && (
                  <span className="pf-tag pf-tag--gray">
                    <i className="bi bi-envelope" /> {labData.labEmail}
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="pf-hero-actions">
            <div className="pf-status-toggle">
              <span className="pf-toggle-label">
                {labData?.labStatus ? "Active" : "Inactive"}
              </span>
              <Form.Check
                checked={!!labData?.labStatus}
                type="switch"
                id="lab-status-switch"
                onChange={handleChangeSwitch}
              />
            </div>

            <button
              className={`pf-edit-btn ${isEditing ? "pf-edit-btn--save" : ""}`}
              onClick={isEditing ? saveProfile : () => setIsEditing(true)}
            >
              {isEditing ? (
                <><Save size={14} /> Stop Editing</>
              ) : (
                <><Edit3 size={14} /> Edit Profile</>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* ── Stat cards ──────────────────────────── */}
      <div className="pf-stats">
        {statItems.map((s) => (
          <div className="pf-stat-card" key={s.label}>
            <div className="pf-stat-icon" style={{ background: s.bg, color: s.color }}>
              {s.icon}
            </div>
            <div className="pf-stat-body">
              <span className="pf-stat-value">{s.value}</span>
              <span className="pf-stat-label">{s.label}</span>
            </div>
          </div>
        ))}
      </div>

      {/* ── Tabs ────────────────────────────────── */}
      <div className="pf-tabs-wrap">
        <div className="pf-tabs">
          {TABS.map((t) => (
            <button
              key={t.id}
              className={`pf-tab ${activeTab === t.id ? "pf-tab--active" : ""}`}
              onClick={() => setActiveTab(t.id)}
            >
              {t.icon}
              <span>{t.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* ── Tab content ─────────────────────────── */}
      <div className="pf-content">

        {activeTab === "profile" && (
          <div className="row g-4">
            <div className="col-md-6">
              <SaveProfile
                saveData={labData}
                labId={labId}
                isEditing={isEditing}
                setIsEditing={setIsEditing}
              />
            </div>
            <div className="col-md-6">
              <SaveLeagalInfo
                legalData={labData}
                labId={labId}
                isEditing={isEditing}
                setIsEditing={setIsEditing}
              />
            </div>
            <div className="col-12">
              <AboutLab
                aboutData={labData}
                labId={labId}
                isEditing={isEditing}
                setIsEditing={setIsEditing}
              />
            </div>
            <div className="col-12">
              <LabHighlights labId={labId} />
            </div>
            <div className="col-12">
              <LabTimings labId={labId}/>
            </div>
          </div>
        )}

        {activeTab === "services" && <ServiceList labId={labId} />}

        {activeTab === "documents" && <ProfileDocTab labId={labId} />}

      </div>
    </div>
  );
};

export default LabProfilePage;
